import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { esvTipDoc, trimNum } from '@/shared/taxTips';
import InfoQ from '@/shared/components/InfoQ';
import {
  vacationOrder,
  vacationCalculation,
  type VacationType,
  type VacationCalculationPreview,
} from '@/api/vacation';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { useConfirm } from '@/components/ConfirmProvider';
import { pickDefaultOrg } from '@/store/defaults';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import TchTable from '@/shared/components/TchTable';

const now = new Date();
const fmt2 = (v: string | number | null | undefined) =>
  v == null ? '—' : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TYPE_LABELS: Record<VacationType, string> = {
  ANNUAL_BASIC: 'Щорічна основна',
  ANNUAL_ADDITIONAL: 'Щорічна додаткова',
  SOCIAL: 'Соціальна',
  UNPAID: 'Без збереження зарплати',
  CREATIVE: 'Творча',
};
const TYPE_ORDER: VacationType[] = ['ANNUAL_BASIC', 'ANNUAL_ADDITIONAL', 'UNPAID'];

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  employee_id: string;
  vacation_type: VacationType;
  vacation_start_date: string;
  vacation_end_date: string;
  reason: string;
  comment: string;
}

const todayISO = now.toISOString().slice(0, 10);
const EMPTY: FormState = {
  organization_id: '', document_number: '', document_date: now.toISOString().slice(0, 16),
  employee_id: '', vacation_type: 'ANNUAL_BASIC',
  vacation_start_date: todayISO, vacation_end_date: todayISO, reason: '', comment: '',
};

function daysBetween(a: string, b: string): number {
  if (!a || !b) return 0;
  const d = (new Date(b).getTime() - new Date(a).getTime()) / 86400000;
  return d >= 0 ? Math.floor(d) + 1 : 0;
}

export default function VacationOrderEditor() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg('documents.vacation_order.create');

  const docQuery = useQuery({
    queryKey: ['vacation-order', id],
    queryFn: () => vacationOrder.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const [preview, setPreview] = useState<VacationCalculationPreview | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [posted, setPosted] = useState(false);
  // Незбережені правки наказа → деактивують «Розрахувати» (preview застарів).
  const [dirty, setDirty] = useState(false);
  // Id вже створеного нарахування цього наказа (щоб ОНОВЛЮВАТИ, а не дублювати).
  const [existingCalcId, setExistingCalcId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Ручний override середньоденної (бухгалтер перебив у табличній частині).
  const [avgOverride, setAvgOverride] = useState('');

  const empQ = useQuery({
    queryKey: ['employees', form.organization_id],
    queryFn: () => refs.employees(form.organization_id, { active_only: true }),
    enabled: !!form.organization_id,
  });
  // V3: залишок днів відпустки для м'якого банера.
  const vacBalanceQ = useQuery({
    queryKey: ['vac-balance', form.employee_id, form.organization_id],
    queryFn: () => vacationOrder.employeeBalance(form.employee_id, form.organization_id),
    enabled: !!form.employee_id && !!form.organization_id,
  });

  useEffect(() => {
    if (isNew) {
      // Авто-вибір org: спершу з availableOrgs (по праву create), fallback — повний
      // список (OWNER/повільний /auth/me). Без fallback organization_id лишався порожнім
      // → save падав «Body missing organization_id».
      // ?employee=&?org= — передзаповнення з картки працівника (2026-08-19).
      const qpEmp = search.get('employee') || '';
      const qpOrg = search.get('org') || '';
      const firstOrg = qpOrg
        || (availableOrgs[0]?.id ?? pickDefaultOrg(orgList.data?.items)?.id ?? '');
      setForm((f) => f.organization_id ? f
        : { ...f, organization_id: firstOrg,
            employee_id: f.employee_id || qpEmp });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id, document_number: d.document_number,
        document_date: d.document_date.slice(0, 16), employee_id: d.employee_id,
        vacation_type: d.vacation_type, vacation_start_date: d.vacation_start_date,
        vacation_end_date: d.vacation_end_date, reason: d.reason ?? '', comment: d.comment ?? '',
      });
      setSavedId(d.id);
      setPosted(d.posted);
      setDirty(false);
      if (d.vacation_calculation_id) {
        setExistingCalcId(d.vacation_calculation_id);
        vacationCalculation.get(d.vacation_calculation_id).then((c) => setPreview(c as any)).catch(() => {});
      }
    }
  }, [isNew, docQuery.data, availableOrgs, orgList.data]);

  const calendarDays = daysBetween(form.vacation_start_date, form.vacation_end_date);
  // ч.1118: тривалість рахує сервер за календарем організації — зі щорічних
  // відпусток святкові дні прибираються (у воєнному режимі календаря свят немає,
  // тож це календарні дні). До відповіді сервера показуємо календарні дні.
  const daysQ = useQuery({
    queryKey: ['vacation-days-count', form.organization_id, form.vacation_type,
      form.vacation_start_date, form.vacation_end_date],
    queryFn: () => vacationOrder.daysCount({
      organization_id: form.organization_id, vacation_type: form.vacation_type,
      start_date: form.vacation_start_date, end_date: form.vacation_end_date,
    }),
    enabled: !!form.organization_id && calendarDays > 0,
  });
  const days = daysQ.data?.days_count ?? calendarDays;
  const holidayDays = daysQ.data?.holiday_days ?? 0;
  const isUnpaid = form.vacation_type === 'UNPAID';

  function buildCreate() {
    return {
      organization_id: form.organization_id,
      document_number: form.document_number || undefined,
      document_date: form.document_date, employee_id: form.employee_id,
      vacation_type: form.vacation_type,
      vacation_start_date: form.vacation_start_date,
      vacation_end_date: form.vacation_end_date,
      days_count: days,
      reason: form.reason || null,
      is_paid: !isUnpaid,
      comment: form.comment || null,
    };
  }
  // PATCH наказа приймає ЛИШЕ ці поля (VacationOrderUpdate, extra=forbid).
  function buildUpdate() {
    return {
      document_date: form.document_date,
      vacation_start_date: form.vacation_start_date,
      vacation_end_date: form.vacation_end_date,
      days_count: days,
      reason: form.reason || null,
      comment: form.comment || null,
    };
  }

  // Зберегти наказ (DRAFT) — як у наказі звільнення.
  const create = useMutation({
    mutationFn: () => vacationOrder.create(buildCreate()),
    onSuccess: (doc) => {
      setSavedId(doc.id); setError(null);
      qc.invalidateQueries({ queryKey: ['vacation-orders'] });
      navigate(`/hr/vacation-orders/${doc.id}`);
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка збереження'),
  });
  // Зберегти і провести наказ.
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await vacationOrder.create(buildCreate());
      try {
        await vacationOrder.post(doc.id);
      } catch (e) {
        // ч.1343: наказ уже створено — відкриваємо його і показуємо, чому не провівся
        // (раніше помилку мовчки ковтали — наказ лишався непроведеним без пояснення).
        navigate(`/hr/vacation-orders/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['vacation-orders'] });
      navigate(`/hr/vacation-orders/${doc.id}`);
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка проведення'),
  });
  // Провести вже збережений наказ.
  const postOrder = useMutation({
    mutationFn: () => vacationOrder.post(savedId!),
    onSuccess: () => {
      setPosted(true); setError(null);
      qc.invalidateQueries({ queryKey: ['vacation-orders'] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка проведення'),
  });
  // Зберегти зміни вже збереженого наказа (PATCH) → дозволяє Розрахувати знову.
  const updateOrder = useMutation({
    mutationFn: () => vacationOrder.update(savedId!, buildUpdate()),
    onSuccess: () => {
      setError(null); setDirty(false); setPreview(null);
      qc.invalidateQueries({ queryKey: ['vacation-orders'] });
      qc.invalidateQueries({ queryKey: ['vacation-order', savedId] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка збереження'),
  });
  // Розпровести наказ.
  const unpostOrder = useMutation({
    mutationFn: () => vacationOrder.unpost(savedId!),
    onSuccess: () => {
      setPosted(false); setError(null);
      qc.invalidateQueries({ queryKey: ['vacation-orders'] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка розпроведення'),
  });
  // Видалити наказ РАЗОМ зі зв'язаним нарахуванням відпускних.
  const removeOrder = useMutation({
    mutationFn: () => vacationOrder.remove(savedId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vacation-orders'] });
      qc.invalidateQueries({ queryKey: ['vacation-calculations'] });
      qc.invalidateQueries({ queryKey: ['accruals-registry'] });
      navigate('/hr-orders');
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка видалення'),
  });

  // Розрахувати — ТIЛЬКИ preview (наказ уже збережено). НЕ створює нарахування.
  const calc = useMutation({
    mutationFn: () => vacationCalculation.preview({
      organization_id: form.organization_id,
      vacation_order_id: savedId!,
      average_daily_override: avgOverride ? avgOverride : null,
    }),
    onSuccess: (r) => {
      setPreview(r); setError(null);
      if (!avgOverride) setAvgOverride(trimNum(r.average_daily_wage));
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка розрахунку'),
  });

  // Зберегти нарахування. Якщо нарахування цього наказа ВЖЕ Є — ОНОВИТИ його
  // (PATCH середньої), інакше створити чернетку (createDraft). Не дублюємо (уник 409).
  const saveCalc = useMutation({
    mutationFn: () => {
      const avg = avgOverride ? avgOverride : '';
      if (existingCalcId) {
        return vacationCalculation.update(existingCalcId, avg);
      }
      return vacationCalculation.createDraft({
        organization_id: form.organization_id,
        vacation_order_id: savedId!,
        average_daily_override: avgOverride ? avgOverride : null,
      });
    },
    onSuccess: (doc) => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['vacation-calculations'] });
      qc.invalidateQueries({ queryKey: ['accruals-registry'] });
      navigate(`/hr/vacation-calculations/${doc.id}`);
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка збереження нарахування'),
  });

  const canSaveOrder = !!form.organization_id && !!form.employee_id && days > 0;
  // Розрахувати — лише для збереженого наказа БЕЗ незбережених правок (dirty).
  const canCalc = !!savedId && !isUnpaid && !posted && !dirty;

  function setF(patch: Partial<FormState>) {
    setForm((f) => ({ ...f, ...patch }));
    if (!posted) { setPreview(null); setDirty(true); }
  }

  // ч.880 (запит власника): «Повʼязані дії» — створити/відкрити нарахування
  // відпускних (та сама мутація, що «зберегти нарахування» у блоці 3: чернетка
  // розрахунку з наказу + перехід у неї; якщо нарахування вже є — відкрити).
  const railActions = [{
    label: existingCalcId ? 'відкрити нарахування' : 'створити нарахування',
    title: !savedId ? 'Спершу збережіть або проведіть наказ'
      : isUnpaid ? 'Відпустка без збереження зарплати — нарахування не робиться'
      : existingCalcId ? 'Нарахування відпускних за цим наказом'
      : 'Створити чернетку нарахування відпускних за цим наказом',
    disabled: !savedId || isUnpaid || saveCalc.isPending,
    onClick: () => {
      if (existingCalcId) navigate(`/hr/vacation-calculations/${existingCalcId}`);
      else saveCalc.mutate();
    },
  }];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 1, filled: form.organization_id ? 1 : 0 },
    { id: 'vac', title: 'Працівник і відпустка', required: 4,
      filled: (form.employee_id ? 1 : 0) + (form.vacation_type ? 1 : 0)
        + (form.vacation_start_date ? 1 : 0) + (form.vacation_end_date ? 1 : 0) },
    { id: 'calc', title: 'Розрахунок відпускних', required: 0, filled: 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];
  const calcTitle = !savedId
    ? 'Спершу збережіть або проведіть наказ — тоді можна розрахувати відпускні'
    : dirty
      ? 'Спершу збережіть зміни наказа («Зберегти») — тоді розрахунок оновиться'
      : 'Перерахунок брутто/ПДФО/ВЗ/ЄСВ за дні відпустки × середню.';

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew && !savedId ? 'Новий наказ (відпустка)' : `Наказ (відпустка) № ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          onBackToList={() => navigate('/hr-orders')}
          onSave={() => isNew ? create.mutate() : updateOrder.mutate()}
          saveDisabled={(isNew && !canSaveOrder) || create.isPending || updateOrder.isPending}
          isPosted={posted}
          isNew={isNew}
          onPost={() => isNew ? createAndPost.mutate() : postOrder.mutate()}
          postDisabled={isNew && !canSaveOrder}
          postPending={createAndPost.isPending || postOrder.isPending}
          postTitle="Зберігає та проводить наказ"
          onUnpost={() => unpostOrder.mutate()}
          unpostPending={unpostOrder.isPending}
          onDelete={() => {
            confirm('Видалити наказ РАЗОМ зі зв\'язаним нарахуванням відпускних?')
              .then((ok) => { if (ok) removeOrder.mutate(); });
          }}
          deletePending={removeOrder.isPending}
          deleteTitle="Видаляє наказ разом зі зв'язаним нарахуванням відпускних"
          onPrint={() => navigate(`/hr/vacation-orders/${savedId}/print`)}
          entriesDoc={{ type: 'doc.vacation_orders', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      {posted && (
        <div style={{ background: 'var(--badge-green-bg)', color: 'var(--badge-green-fg)', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>✓ Наказ проведено.</div>
      )}

      {error && (
        <div style={{ background: 'var(--badge-red-bg)', color: '#b00', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>{error}</div>
      )}

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew && !savedId}
          doc={{ type: 'doc.vacation_orders', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 8' }}>
          <label>Організація *</label>
          <select value={form.organization_id} disabled={!isNew || !!savedId}
            onChange={(e) => setF({ organization_id: e.target.value, employee_id: '' })}>
            <option value="">—</option>
            {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата документа</label>
          <DatePopover value={form.document_date.slice(0, 10)} disabled={posted}
            onChange={(v) => setF({ document_date: `${v}T00:00` })} />
        </div>
      </div>
      </DocBlock>

      <DocBlock index={2} title="Працівник і відпустка">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 7' }}>
          <label>Працівник *</label>
          <select value={form.employee_id} disabled={posted}
            onChange={(e) => setF({ employee_id: e.target.value })}>
            <option value="">—</option>
            {empQ.data?.map((e) => <option key={e.id} value={e.id}>{e.name}{e.code ? ` — ${e.code}` : ''}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label>Вид відпустки *</label>
          <select value={form.vacation_type} disabled={posted}
            onChange={(e) => setF({ vacation_type: e.target.value as VacationType })}>
            {TYPE_ORDER.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
          </select>
          {isUnpaid && (
            <small style={{ color: 'var(--muted)' }}>Без збереження зарплати — розрахунок не робиться.</small>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field" style={{ minWidth: 150 }}>
          <label>Дата початку *</label>
          <DatePopover value={form.vacation_start_date} disabled={posted} onChange={(v) => setF({ vacation_start_date: v })} />
        </div>
        <div className="field" style={{ minWidth: 150 }}>
          <label>Дата закінчення *</label>
          <DatePopover value={form.vacation_end_date} disabled={posted} onChange={(v) => setF({ vacation_end_date: v })} />
        </div>
        <div className="field" style={{ width: 90 }}>
          <label>Днів</label>
          <input value={days} readOnly disabled style={{ textAlign: 'center', background: 'var(--bg-soft)' }}
            title={holidayDays > 0
              ? `Календарних днів ${calendarDays}, з них святкових і неробочих за календарем організації — ${holidayDays}: вони не входять у тривалість відпустки`
              : undefined} />
        </div>
        <div className="field" style={{ minWidth: 200 }}>
          <label>Підстава</label>
          <input value={form.reason} disabled={posted} placeholder="заява працівника…"
            onChange={(e) => setF({ reason: e.target.value })} />
        </div>
      </div>

      {/* V3: м'який банер залишку днів (тільки щорічні відпустки). */}
      {form.employee_id && vacBalanceQ.data &&
        (form.vacation_type === 'ANNUAL_BASIC' || form.vacation_type === 'ANNUAL_ADDITIONAL') && (() => {
        const bal = Number(form.vacation_type === 'ANNUAL_BASIC'
          ? vacBalanceQ.data.annual_basic_balance
          : vacBalanceQ.data.annual_additional_balance);
        const notEnough = days > bal;
        return (
          <div style={{ marginTop: '0.6rem', padding: '0.5rem 0.75rem', borderRadius: 6,
            background: notEnough ? '#fef3c7' : '#ecfdf5',
            border: `1px solid ${notEnough ? '#f59e0b' : '#a7f3d0'}`, fontSize: '0.85rem' }}>
            Залишок днів цього виду відпустки: <strong>{bal.toFixed(2)}</strong>
            {notEnough && (
              <span style={{ color: 'var(--badge-amber-fg)' }}>
                {' '}⚠ Запитується {days} дн. — більше за залишок. Дозволено (авансова
                відпустка), але перевірте.
              </span>
            )}
          </div>
        );
      })()}

      </DocBlock>

      {/* Розрахунок відпускних — кнопка активна ЛИШЕ після збереження наказа
          (як у наказі звільнення). Розрахувати = preview, НЕ створює нарахування.
          ч.875: «розрахувати» — у заголовку блоку. */}
      <DocBlock index={3} title="Розрахунок відпускних"
        actions={!posted && !isUnpaid ? (
          <button className="primary" disabled={!canCalc || calc.isPending}
            onClick={() => calc.mutate()} title={calcTitle}>
            розрахувати
          </button>
        ) : undefined}>
      {isUnpaid && (
        <p style={{ color: 'var(--muted)', fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
          Відпустка без збереження зарплати — розрахунок не робиться.
        </p>
      )}
      {!posted && !isUnpaid && (
        <div style={{ marginBottom: '0.4rem' }}>
          <div className="field" style={{ width: 180 }}>
            <label>Середньоденна {preview?.average_from_import ? '⚠' : ''}</label>
            <input type="number" step="0.01" value={avgOverride} disabled={!savedId}
              placeholder="авто з заробітку"
              onChange={(e) => { setAvgOverride(e.target.value); setPreview(null); }} />
          </div>
          {!savedId && (
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0 }}>
              Кнопка «розрахувати» стане активною після збереження або проведення наказа.
            </p>
          )}
          {savedId && dirty && (
            <p style={{ color: 'var(--badge-amber-fg)', fontSize: '0.8rem', margin: 0 }}>
              Ви змінили дані наказа. Натисніть «зберегти» — після цього можна
              перерахувати відпускні.
            </p>
          )}
        </div>
      )}

      {preview && (
        <div style={{ marginTop: '0.5rem' }}>
          {/* Таблична частина — дизайн як у нарахуванні ЗП/компенсації. */}
          <div style={{ overflowX: 'auto' }}>
          <TchTable>
            <thead>
              <tr style={{ textAlign: 'right' }}>
                <th style={{ textAlign: 'left' }}>Працівник</th>
                <th style={{ width: 150 }}>Період розрахунку</th>
                <th style={{ width: 95 }}>Заробіток 12 міс</th>
                <th style={{ width: 95 }}>
                  Середня{preview.average_from_import ? ' ⚠' : ''}
                </th>
                <th style={{ width: 60 }}>Днів</th>
                <th style={{ width: 95 }}>Брутто</th>
                <th style={{ width: 95 }}>ПДФО</th>
                <th style={{ width: 95 }}>ВЗ</th>
                <th style={{ width: 95 }}>ЄСВ</th>
                <th style={{ width: 100 }}>До виплати</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  {empQ.data?.find((e) => e.id === form.employee_id)?.name ?? '—'}
                </td>
                <td style={{ textAlign: 'right' }}>{preview.calc_period_start} — {preview.calc_period_end}</td>
                <td style={{ textAlign: 'right' }}>{fmt2(preview.total_earnings_12m)}</td>
                <td style={{ textAlign: 'right' }}>{fmt2(preview.average_daily_wage)}</td>
                <td style={{ textAlign: 'right' }}>{preview.days_count}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt2(preview.vacation_gross_amount)}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                      value={trimNum(preview.pdfo_amount)}
                      style={{ textAlign: 'right', width: '100%', background: 'var(--bg-soft)', color: 'var(--text-secondary)' }} />
                    <InfoQ title={`ПДФО — ${trimNum(preview.pdfo_rate)}% від брутто.`} />
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                      value={trimNum(preview.wz_amount)}
                      style={{ textAlign: 'right', width: '100%', background: 'var(--bg-soft)', color: 'var(--text-secondary)' }} />
                    <InfoQ title={`Військовий збір — ${trimNum(preview.wz_rate)}% від брутто.`} />
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                      value={trimNum(preview.esv_amount)}
                      style={{ textAlign: 'right', width: '100%', background: 'var(--bg-soft)', color: 'var(--text-secondary)' }} />
                    <InfoQ title={esvTipDoc(preview.esv_amount, preview.esv_rate)} />
                  </div>
                </td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt2(preview.net_amount)}</td>
              </tr>
            </tbody>
          </TchTable>
          </div>

          {/* Зберегти нарахування — створює чернетку розрахунку (як у наказі звільнення). */}
          {!posted && savedId && (
            <button className="primary" style={{ marginTop: '0.5rem' }}
              disabled={saveCalc.isPending}
              onClick={() => saveCalc.mutate()}>
              зберегти нарахування
            </button>
          )}

          {/* ч.1118: з чого взято середню (Порядок №100) — період, оклад, виключені дні. */}
          {preview.calc_note && (
            <div style={{ marginTop: '0.5rem', padding: '0.4rem 0.6rem', borderRadius: 6,
              background: 'var(--bg-soft)', border: '1px solid var(--border)', fontSize: '0.8rem',
              whiteSpace: 'pre-line' }}>
              {preview.calc_note}
            </div>
          )}

          {preview.average_from_import && (
            <div style={{ marginTop: '0.5rem', padding: '0.4rem 0.6rem', borderRadius: 6,
              background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)', fontSize: '0.8rem',
              color: 'var(--badge-amber-fg)' }}>
              ⚠ Середня врахувала імпортовані з 1С нарахування — перевірте суму.
            </div>
          )}

          {(preview.monthly_parts?.length ?? 0) > 1 && (
            <div style={{ marginTop: '0.75rem' }}>
              <h4 style={{ margin: '0 0 0.3rem', fontSize: '0.85rem', color: 'var(--badge-amber-fg)' }}>
                По місяцях (перехідна відпустка — для звітності)
              </h4>
              <TchTable>
                <thead>
                  <tr style={{ textAlign: 'right' }}>
                    <th style={{ textAlign: 'left' }}>Місяць</th>
                    <th>Днів</th>
                    <th>Брутто</th>
                    <th>ПДФО</th>
                    <th>ВЗ</th>
                    <th>ЄСВ</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.monthly_parts!.map((m) => (
                    <tr key={`${m.period_year}-${m.period_month}`} style={{ textAlign: 'right', borderTop: '1px solid #eee' }}>
                      <td style={{ textAlign: 'left' }}>
                        {m.period_year}-{String(m.period_month).padStart(2, '0')}
                      </td>
                      <td>{m.days_in_month}</td>
                      <td>{fmt2(m.gross_amount)}</td>
                      <td>{fmt2(m.pdfo_amount)}</td>
                      <td>{fmt2(m.wz_amount)}</td>
                      <td>{fmt2(m.esv_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </TchTable>
            </div>
          )}
        </div>
      )}
      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        {/* ч.875: поле коментаря — state/payload були, input у формі не було. */}
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment} disabled={posted}
                 onChange={(e) => setF({ comment: e.target.value })} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType={'doc.vacation_orders'} documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
    </div>
  );
}

